import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bug, ExternalLink, RefreshCw, Search, ShieldAlert, Zap, TrendingUp, Building2 } from "lucide-react";

const SEVERITIES = [
  { key: "all", label: "All", color: "#00F0FF" },
  { key: "kev", label: "KEV", color: "#FF2E63" },
  { key: "critical", label: "Critical", color: "#FF2E63" },
  { key: "high", label: "High", color: "#FF6B35" },
  { key: "medium", label: "Medium", color: "#FFC542" },
  { key: "low", label: "Low", color: "#00F0FF" },
  { key: "zeroday", label: "Zero-Day", color: "#B57BFF" },
];

const SEV_COLOR = {
  critical: "#FF2E63", high: "#FF6B35", medium: "#FFC542", low: "#00F0FF", unknown: "#8E9196",
  zeroday: "#B57BFF",
};

export default function CVETracker() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [severity, setSeverity] = useState("all");
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState("");
  const [vendorFilter, setVendorFilter] = useState([]);
  const [kevSince, setKevSince] = useState(null);
  const [kevWindow, setKevWindow] = useState("7d");
  const [platform, setPlatform] = useState("all");

  // Load persisted vendor preset on mount
  useEffect(() => {
    api.get("/cve-tracker/my-vendors")
      .then((r) => { if (r.data?.vendors?.length) setVendorFilter(r.data.vendors); })
      .catch(() => {});
  }, []);

  // Load KEV Since-Added dashboard on window change
  useEffect(() => {
    api.get("/cve-tracker/kev-since-added", { params: { window: kevWindow, limit: 60 } })
      .then((r) => setKevSince(r.data))
      .catch(() => setKevSince(null));
  }, [kevWindow]);

  async function saveVendorPreset() {
    try {
      await api.put("/cve-tracker/my-vendors", { vendors: vendorFilter });
      toast.success(`Saved ${vendorFilter.length} vendors as your default`);
    } catch (_e) { toast.error("Save failed"); }
  }

  async function load() {
    setLoading(true);
    try {
      const params = { since_days: days, severity, limit: 200 };
      if (vendorFilter.length) params.vendors = vendorFilter.join(",");
      if (platform !== "all") params.platform = platform;
      const r = await api.get("/cve-tracker/summary", { params });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load CVE data");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [severity, days, vendorFilter, platform]);

  async function forceRefresh() {
    setRefreshing(true);
    try {
      await api.post("/cve-tracker/refresh");
      toast.success("Refetched CVE feed");
      await load();
    } catch (_e) { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  }

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || !data?.items) return data?.items || [];
    return data.items.filter((it) =>
      (it.cve_id || "").toLowerCase().includes(q) ||
      (it.summary || "").toLowerCase().includes(q) ||
      (it.vendors || []).some((v) => v.toLowerCase().includes(q)) ||
      (it.products || []).some((p) => p.toLowerCase().includes(q))
    );
  }, [data, search]);

  return (
    <div className="min-h-screen bg-[#060606] text-white p-8" data-testid="cve-tracker-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Bug className="w-5 h-5 text-[#FF6B35]" strokeWidth={1.75} />
              <h1 className="font-display font-black text-3xl tracking-tight">CVE TRACKER</h1>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] border border-[#00F0FF]/40 rounded-sm px-1.5 py-0.5">LIVE</span>
            </div>
            <p className="text-sm text-[#8E9196]">
              CIRCL vulnerability feed · normalized to CVSS v3.1 · zero-day flags inferred from language + CVSS ≥ 9 in last 7 days
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              data-testid="cve-days-select"
              className="bg-black border border-white/10 focus:border-[#00F0FF] rounded-sm px-3 py-2 text-xs font-mono outline-none"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              onClick={forceRefresh}
              disabled={refreshing}
              data-testid="cve-refresh-btn"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-white/30 px-3 py-2 rounded-sm text-[10px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white disabled:opacity-40"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="cve-stats">
          <StatCard label={`Reports · ${days}d`} value={data?.stats?.total ?? "—"} color="#00F0FF" testid="stat-total" />
          <StatCard label="Daily mean · 7d" value={data?.stats?.daily_mean_7d ?? "—"} color="#00F0FF" testid="stat-daily-mean" trend={<TrendingUp className="w-3 h-3" />} />
          <StatCard label="Critical share" value={`${data?.stats?.critical_share_pct ?? 0}%`}
            color={data?.stats?.critical_share_pct > 20 ? "#FF2E63" : "#FFC542"}
            testid="stat-critical-share" />
          <StatCard label="CISA KEV · 7d" value={data?.stats?.kev_count ?? 0} color="#FF2E63" icon={<ShieldAlert className="w-3 h-3" />} testid="stat-kev" />
          <StatCard label="Zero-days · 7d" value={data?.stats?.zeroday_count ?? "—"} color="#B57BFF" icon={<Zap className="w-3 h-3" />} testid="stat-zerodays" />
          <StatCard label="Published today" value={data?.stats?.active_now ?? "—"} color="#00F0FF" testid="stat-today" />
        </div>

        {data?.kev?.catalogue_size > 0 && (
          <div className="mb-4 text-[10px] font-mono text-[#52525B] flex items-center gap-3" data-testid="kev-footer">
            <span>CISA KEV catalogue: <span className="text-[#FF2E63]">{data.kev.catalogue_size} CVEs</span></span>
            <span>Refreshed: {data.kev.last_refreshed ? new Date(data.kev.last_refreshed).toLocaleString() : "loading…"}</span>
            <button onClick={async () => {
              try { await api.post("/cve-tracker/kev-refresh"); toast.success("KEV refreshed"); await load(); }
              catch { toast.error("KEV refresh failed"); }
            }} data-testid="kev-refresh"
                    className="ml-auto text-[#8E9196] hover:text-white uppercase tracking-widest">Refresh KEV</button>
          </div>
        )}

        {/* Severity distribution + daily volume side-by-side */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="border border-white/10 bg-[#0a0a0a] rounded-sm p-4" data-testid="severity-donut">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-3">Severity distribution</div>
            {data?.severity_counts && (
              <div className="space-y-2">
                {["critical", "high", "medium", "low"].map((sev) => {
                  const total = Object.values(data.severity_counts).reduce((a, b) => a + b, 0) || 1;
                  const count = data.severity_counts[sev] || 0;
                  const pct = Math.round((count / total) * 100);
                  return (
                    <div key={sev} data-testid={`sev-bar-${sev}`}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: SEV_COLOR[sev] }}>{sev}</span>
                        <span className="text-[10px] font-mono text-[#8E9196]">{count} · {pct}%</span>
                      </div>
                      <div className="h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                        <div className="h-full" style={{ width: `${pct}%`, background: SEV_COLOR[sev] }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="lg:col-span-2 border border-white/10 bg-[#0a0a0a] rounded-sm p-4" data-testid="daily-volume">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-3">Daily volume · stacked by severity</div>
            {data?.daily && <DailyVolumeChart daily={data.daily} />}
          </div>
        </div>

        {/* Zero-days panel */}
        {data?.zerodays && data.zerodays.length > 0 && (
          <div className="mb-6 border border-[#B57BFF]/40 bg-[#B57BFF]/[0.03] rounded-sm p-4" data-testid="zeroday-panel">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-[#B57BFF]" />
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#B57BFF]">Zero-day watchlist · last 7 days</div>
              <span className="text-[10px] font-mono text-[#B57BFF]">({data.zerodays.length})</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {data.zerodays.map((z) => (
                <a key={z.cve_id} href={z.url} target="_blank" rel="noopener noreferrer"
                   data-testid={`zeroday-${z.cve_id}`}
                   className="block border border-white/10 bg-black/50 hover:border-[#B57BFF]/50 rounded-sm px-3 py-2 transition-colors">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-sm text-white">{z.cve_id}</span>
                    <div className="flex items-center gap-1">
                      {z.cvss != null && (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm" style={{ background: SEV_COLOR[z.severity], color: "#000" }}>
                          CVSS {z.cvss}
                        </span>
                      )}
                      {z.zeroday && (
                        <span className="text-[9px] font-mono uppercase tracking-widest text-[#B57BFF] border border-[#B57BFF]/40 px-1 rounded-sm">0-DAY</span>
                      )}
                      {z.kev && (
                        <span className="text-[9px] font-mono uppercase tracking-widest text-black bg-[#FF2E63] px-1 rounded-sm">KEV</span>
                      )}
                    </div>
                  </div>
                  <div className="text-[10px] text-[#8E9196] line-clamp-2">{z.summary || "—"}</div>
                  {(z.matched_customers || []).length > 0 && (
                    <div className="mt-1 flex items-center gap-1 text-[10px] font-mono text-[#FF6B35]" data-testid={`zeroday-affected-${z.cve_id}`}>
                      <Building2 className="w-3 h-3" /> Affects: {z.matched_customers.slice(0, 2).map((m) => m.customer_name).join(", ")}
                      {z.matched_customers.length > 2 && ` +${z.matched_customers.length - 2}`}
                    </div>
                  )}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* KEV Since-Added Dashboard */}
        <div className="mb-6 border border-[#FF2E63]/30 bg-[#FF2E63]/[0.03] rounded-sm p-4" data-testid="kev-since-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-[#FF2E63]" />
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF2E63]">CISA KEV · newly added</div>
              <span className="text-[10px] font-mono text-[#FF2E63]">({kevSince?.total ?? 0})</span>
            </div>
            <div className="flex items-center gap-1 border border-white/10 rounded-sm p-0.5">
              {["24h", "7d", "30d", "all"].map((w) => (
                <button key={w} onClick={() => setKevWindow(w)}
                        data-testid={`kev-window-${w}`}
                        className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm ${
                          kevWindow === w ? "bg-[#FF2E63] text-black" : "text-[#8E9196] hover:text-white"
                        }`}>{w}</button>
              ))}
            </div>
          </div>
          {(!kevSince || !kevSince.grouped?.length) ? (
            <div className="text-[11px] font-mono text-[#52525B]">No new KEV additions in this window.</div>
          ) : (
            <div className="space-y-3">
              {kevSince.grouped.slice(0, 4).map((day) => (
                <div key={day.date_added} data-testid={`kev-day-${day.date_added}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF6B35] mb-1">
                    Added {day.date_added} · {day.cves.length} CVE{day.cves.length !== 1 ? "s" : ""}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {day.cves.map((c) => (
                      <div key={c.cve_id} className="border border-white/10 rounded-sm px-2 py-1.5 flex items-start gap-2 bg-black/40" data-testid={`kev-added-${c.cve_id}`}>
                        <a href={`https://nvd.nist.gov/vuln/detail/${c.cve_id}`} target="_blank" rel="noopener noreferrer"
                           className="font-mono text-xs text-white hover:text-[#FF2E63] shrink-0">{c.cve_id}</a>
                        {c.known_ransomware_use === "Known" && (
                          <span className="text-[9px] font-mono uppercase tracking-widest bg-[#FF2E63] text-black px-1 rounded-sm shrink-0">ransomware</span>
                        )}
                        <div className="text-[10px] text-[#8E9196] flex-1 line-clamp-1">
                          <span className="text-[#B57BFF]">{c.vendor_project}</span> · {c.product} — {c.vulnerability_name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Vendor filter */}
        {data?.vendors_available?.length > 0 && (
          <div className="mb-4 border border-white/10 bg-[#0a0a0a] rounded-sm p-3" data-testid="vendor-filter">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#B57BFF]">Vendors / products</span>
              {vendorFilter.length > 0 && (
                <>
                  <button onClick={() => setVendorFilter([])}
                          data-testid="vendor-clear"
                          className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white">
                    Clear all
                  </button>
                  <button onClick={saveVendorPreset}
                          data-testid="vendor-save-preset"
                          className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] hover:text-white">
                    Save as my default
                  </button>
                </>
              )}
              <span className="ml-auto text-[10px] font-mono text-[#52525B]">
                {vendorFilter.length ? `${vendorFilter.length} selected` : "click to scope · shows top 20"}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {data.vendors_available.slice(0, 20).map((v) => {
                const active = vendorFilter.includes(v.name);
                return (
                  <button
                    key={v.name}
                    onClick={() => setVendorFilter(active ? vendorFilter.filter((x) => x !== v.name) : [...vendorFilter, v.name])}
                    data-testid={`vendor-chip-${v.name}`}
                    className={`inline-flex items-center gap-1 px-2 py-1 border rounded-sm text-[10px] font-mono transition-colors ${
                      active
                        ? "bg-[#B57BFF] text-black border-[#B57BFF]"
                        : "border-white/10 text-[#8E9196] hover:text-white hover:border-white/30"
                    }`}
                  >
                    {v.name} <span className="opacity-60">{v.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Severity tabs */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-1 border border-white/10 rounded-sm bg-[#0a0a0a] p-0.5" data-testid="cve-severity-tabs">
            {SEVERITIES.map((s) => {
              const count = s.key === "all"
                ? data?.stats?.total || 0
                : s.key === "zeroday"
                  ? data?.stats?.zeroday_count || 0
                  : s.key === "kev"
                    ? data?.stats?.kev_count || 0
                    : (data?.severity_counts?.[s.key] || 0);
              return (
                <button
                  key={s.key}
                  onClick={() => setSeverity(s.key)}
                  data-testid={`cve-tab-${s.key}`}
                  className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest rounded-sm transition-colors ${
                    severity === s.key ? "text-black" : "text-[#8E9196] hover:text-white"
                  }`}
                  style={severity === s.key ? { background: s.color } : {}}
                >
                  {s.label} <span className="opacity-70">{count}</span>
                </button>
              );
            })}
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#52525B]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search CVE ID / vendor / product…"
              data-testid="cve-search"
              className="pl-8 pr-3 py-2 bg-[#0a0a0a] border border-white/10 focus:border-[#00F0FF] rounded-sm text-xs font-mono outline-none w-72"
            />
          </div>
        </div>

        {/* Platform tiles row — filters items by OS / cloud / stack family */}
        {data?.platform_counts && (
          <div className="mb-4" data-testid="cve-platform-tiles">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">
              Platform breakdown <span className="text-[#52525B]">(click to filter)</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button
                key="all"
                onClick={() => setPlatform("all")}
                data-testid="cve-platform-all"
                className={`px-2.5 py-1.5 border rounded-sm font-mono text-[10px] uppercase tracking-widest transition-colors ${
                  platform === "all"
                    ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10"
                    : "border-white/10 text-[#8E9196] hover:border-white/30 hover:text-white"
                }`}>
                All <span className="opacity-60">{data?.stats?.total || 0}</span>
              </button>
              {(data.platform_order || []).map((key) => {
                const count = data.platform_counts[key] || 0;
                if (count === 0) return null;
                const active = platform === key;
                const label = {
                  windows: "Windows", linux: "Linux", macos: "macOS",
                  aws: "AWS", azure: "Azure", gcp: "GCP", vmware: "VMware",
                  network: "Network", web_servers: "Web Servers",
                  databases: "Databases", containers: "Containers",
                  mobile: "Mobile", other: "Other",
                }[key] || key;
                const accent = {
                  windows: "#00A4EF", linux: "#FFCC00", macos: "#C7C7CC",
                  aws: "#FF9900", azure: "#0089D6", gcp: "#34A853",
                  vmware: "#00A2ED", network: "#B57BFF", web_servers: "#00F0FF",
                  databases: "#FF6B6B", containers: "#5470FF", mobile: "#8E9196",
                  other: "#52525B",
                }[key] || "#8E9196";
                return (
                  <button
                    key={key}
                    onClick={() => setPlatform(active ? "all" : key)}
                    data-testid={`cve-platform-${key}`}
                    style={active ? { borderColor: accent, color: accent, background: `${accent}15` } : {}}
                    className={`px-2.5 py-1.5 border rounded-sm font-mono text-[10px] uppercase tracking-widest transition-colors ${
                      active ? "" : "border-white/10 text-[#8E9196] hover:border-white/30 hover:text-white"
                    }`}
                  >
                    {label} <span className="opacity-60">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* CVE table */}
        <div className="border border-white/10 bg-[#0a0a0a] rounded-sm" data-testid="cve-table">
          <div className="grid grid-cols-[140px_80px_100px_1fr_180px_120px] px-4 py-2 border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#52525B]">
            <div>CVE</div>
            <div>CVSS</div>
            <div>Severity</div>
            <div>Summary</div>
            <div>Affected products</div>
            <div>Published</div>
          </div>
          {loading && <div className="p-6 text-[11px] font-mono text-[#52525B]">Loading…</div>}
          {!loading && filteredItems.length === 0 && (
            <div className="p-6 text-[11px] font-mono text-[#52525B]">No CVEs matching the filters.</div>
          )}
          {filteredItems.slice(0, 200).map((it) => (
            <div key={it.cve_id}
                 className="grid grid-cols-[140px_80px_100px_1fr_180px_120px] items-start px-4 py-3 border-b border-white/[0.04] last:border-0 text-xs hover:bg-white/[0.02]"
                 data-testid={`cve-row-${it.cve_id}`}>
              <div>
                <a href={it.url} target="_blank" rel="noopener noreferrer"
                   className="font-mono text-white hover:text-[#00F0FF] inline-flex items-center gap-1">
                  {it.cve_id} <ExternalLink className="w-3 h-3 opacity-40" />
                </a>
                {it.zeroday && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-[#B57BFF] border border-[#B57BFF]/40 px-1 rounded-sm">
                    <Zap className="w-2.5 h-2.5" /> 0-day
                  </div>
                )}
                {it.kev && (
                  <div className="mt-1 ml-1 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-black bg-[#FF2E63] px-1 rounded-sm" data-testid={`kev-badge-${it.cve_id}`}>
                    <ShieldAlert className="w-2.5 h-2.5" /> KEV
                  </div>
                )}
                {(it.matched_customers || []).length > 0 && (
                  <div className="mt-1 text-[9px] font-mono text-[#FF6B35]" title={it.matched_customers.map((m) => m.customer_name).join(", ")}>
                    ↳ {it.matched_customers.length} customer{it.matched_customers.length > 1 ? "s" : ""} affected
                  </div>
                )}
              </div>
              <div className="font-mono text-sm" style={{ color: SEV_COLOR[it.severity] }}>{it.cvss ?? "—"}</div>
              <div>
                <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm"
                      style={{ background: SEV_COLOR[it.severity], color: "#000" }}>
                  {it.severity}
                </span>
              </div>
              <div className="text-[#e5e5e5] line-clamp-2 pr-4">{it.summary || "—"}</div>
              <div className="text-[10px] font-mono text-[#8E9196]">
                {(it.products || []).slice(0, 3).join(", ") || "—"}
              </div>
              <div className="text-[10px] font-mono text-[#8E9196]">
                {it.published ? new Date(it.published).toLocaleDateString() : "—"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, testid, icon, trend }) {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] rounded-sm p-4" data-testid={testid}>
      <div className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196] mb-1 flex items-center gap-1">
        {icon}{label}{trend}
      </div>
      <div className="font-display font-black text-2xl" style={{ color }}>{value}</div>
    </div>
  );
}

function DailyVolumeChart({ daily }) {
  const max = Math.max(1, ...daily.map((d) => d.total));
  return (
    <div className="flex items-end gap-0.5 h-32" data-testid="daily-chart">
      {daily.map((d) => (
        <div key={d.date} className="flex-1 flex flex-col justify-end min-w-[3px] group relative"
             title={`${d.date} — CRIT ${d.critical} · HIGH ${d.high} · MED ${d.medium} · LOW ${d.low}`}>
          {d.critical > 0 && <div style={{ height: `${(d.critical / max) * 100}%`, background: SEV_COLOR.critical }} className="w-full" />}
          {d.high > 0 && <div style={{ height: `${(d.high / max) * 100}%`, background: SEV_COLOR.high }} className="w-full" />}
          {d.medium > 0 && <div style={{ height: `${(d.medium / max) * 100}%`, background: SEV_COLOR.medium }} className="w-full" />}
          {d.low > 0 && <div style={{ height: `${(d.low / max) * 100}%`, background: SEV_COLOR.low }} className="w-full" />}
          {d.total === 0 && <div className="w-full h-[1px] bg-white/5" />}
        </div>
      ))}
    </div>
  );
}
